import React from "react";
import { useRouter } from "next/router";
import { _get } from "../../../utils/request";
import { getPaymentFlags } from "../../../utils/paymentFlags";
import { loadCatalog } from "../../../i18n";
import Head from "../../../components/Head";
import { useUserInfo } from "../../../hooks/useUserInfo";
import { useFreshStreamerData } from "../../../hooks/useFreshStreamerData";
import { DonationForm } from "../../../views/DonationForm";
import { VoucherForm } from "../../../views/VoucherForm";
import { ListWishlistPublic } from "../../../views/WishlistPublic/ListWishlistPublic";
import { SoundboardForm } from "../../../views/SoundboardForm";
import { TokoForm } from "../../../views/TokoForm";
import {
  StreamerPageLayout,
  useStreamerTabs,
  getPaymentFlagsFromData,
  resolveGameVoucherEnabled,
} from "../../../views/StreamerPage/StreamerPageLayout";
import { MiniGamesTab } from "../../../views/MiniGames/MiniGamesTab";

export async function getStaticPaths() {
  return { paths: [], fallback: true };
}

export async function getStaticProps({ params, ...ctx }) {
  const { username } = params;
  const translation = await loadCatalog(ctx.locale);
  const revalidate = { revalidate: 1 };
  const paymentFlags = await getPaymentFlags();

  try {
    const {
      json: { data: apiData },
    } = await _get(`users/${username}`);

    return {
      props: { data: { ...apiData, ...paymentFlags }, translation },
      ...revalidate,
    };
  } catch (error) {
    if (error.response) {
      const { status, json } = error.response;
      return {
        props: { data: { username }, error: { json, status }, translation },
        ...revalidate,
      };
    }
    return {
      props: {
        data: { username },
        translation,
        error: { message: "UNKNOWN_ERROR", status: 500 },
      },
      ...revalidate,
    };
  }
}

const TokoPage = ({ data: staticData = {}, error }) => {
  const { data, isFresh } = useFreshStreamerData(staticData, error);
  const router = useRouter();
  const { data: userInfo = {} } = useUserInfo({}, () => {});
  const { username: currUsername, email: currEmail, profile_picture: proPic } = userInfo;
  const {
    username,
    id,
    plus,
    overlay = {},
    vote = {},
    currency,
    transaction_fee_policy: transactionFeePolicy,
  } = data;

  const { isTopUp, gameVoucherEnabled, hasWishlist, soundboard, hasStore, miniGamesEnabled } = useStreamerTabs(data);

  // The /toko index only makes sense when the store is active. Wait for the
  // fresh client fetch before redirecting so stale static data can't bounce us.
  const noStore = !isTopUp && !hasStore;
  React.useEffect(() => {
    if (!router.isReady || !username || !isFresh) return;
    if (noStore) {
      router.replace(`/${username}`);
    }
  }, [router.isReady, noStore, isFresh, username]);

  if (!router.isReady) return null;
  if (noStore) return null;
  const paymentFlags = getPaymentFlagsFromData(data);

  const head = (
    <Head title={`Toko ${username}`}>
      <meta name="description" content={``} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://saweria.co/${username}/toko`} />
      <meta property="og:title" content={`Toko ${username}`} />
      <meta property="og:description" content={``} />
      <meta property="og:image" content={proPic ? proPic : "https://saweria.co/twitter_card.png"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@saweriaco" />
      <meta name="twitter:title" content={`Toko ${username}`} />
      <meta name="twitter:description" content={``} />
      <meta name="twitter:image" content={proPic ? proPic : "https://saweria.co/twitter_card.png"} />
    </Head>
  );

  const panels = [];

  if (!isTopUp) {
    panels.push(
      <DonationForm
        key="donation"
        plus={plus}
        overlay={overlay}
        vote={vote}
        streamerId={id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
        challenge={data.challenge}
      />
    );
  }

  if (isTopUp || gameVoucherEnabled) {
    panels.push(
      <VoucherForm
        key="voucher"
        plus={plus}
        overlay={overlay}
        vote={vote}
        streamerId={id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
      />
    );
  }

  if (!isTopUp && hasStore) {
    panels.push(
      <TokoForm
        key="toko"
        plus={plus}
        overlay={overlay}
        vote={vote}
        streamerId={id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        streamerUsername={username}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
      />
    );
  }

  if (!isTopUp && miniGamesEnabled) {
    panels.push(
      <MiniGamesTab
        key="mini-games"
        streamerUsername={data.username}
        streamerId={data.id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
      />
    );
  }

  if (!isTopUp && hasWishlist) {
    panels.push(
      <ListWishlistPublic
        key="wishlist"
        plus={plus}
        overlay={overlay}
        vote={vote}
        streamerId={id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        streamerUsername={username}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
      />
    );
  }

  if (!isTopUp && soundboard) {
    panels.push(
      <SoundboardForm
        key="soundboard"
        plus={plus}
        overlay={overlay}
        vote={vote}
        streamerId={id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
      />
    );
  }

  return (
    <StreamerPageLayout data={data} error={error} head={head}>
      {panels}
    </StreamerPageLayout>
  );
};

export default TokoPage;
